import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useMessagePopupStore } from "../store";

/** `?message_applicant=<id>` on an application page opens that chat. */
const APPLICANT_PARAM = "message_applicant";
/** `?message_contact=<id>` opens a contact's conversation from anywhere. */
const CONTACT_PARAM = "message_contact";

const APPLICATION_PATH = /^\/applications\/(\d+)/;

const positiveInt = (value: string | null) => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

/**
 * Opens the pop-up from a URL, then strips the param so a re-render or a back
 * navigation cannot re-open it.
 *
 * This is what the "new chat message" notification emails link to: the report
 * chat tab they used to point at no longer exists (CRM-1133). Only ONE query
 * param is used on purpose — email clients escape the `&` before a second one,
 * which drops it — so the application id is read from the path instead.
 */
export const useMessagePopupDeepLink = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const open = useMessagePopupStore((state) => state.open);

  const applicantParam = searchParams.get(APPLICANT_PARAM);
  const contactParam = searchParams.get(CONTACT_PARAM);

  useEffect(() => {
    if (!applicantParam && !contactParam) return;

    const applicantId = positiveInt(applicantParam);
    const contactId = positiveInt(contactParam);

    setSearchParams(
      (prev) => {
        prev.delete(APPLICANT_PARAM);
        prev.delete(CONTACT_PARAM);
        return prev;
      },
      { replace: true }
    );

    if (!applicantId && !contactId) return;

    open({
      applicantId,
      contactId,
      applicationId: positiveInt(APPLICATION_PATH.exec(pathname)?.[1] ?? null),
      channel: "chat"
    });
  }, [applicantParam, contactParam, pathname, setSearchParams, open]);
};
